/**
 * Traduções nossas — tela de Orçamento: automações de categoria (metas,
 * modelos), mensagens de validação e o menu de ordenação.
 *
 * A chave é a frase em inglês, byte a byte como está em `locale/en.json`.
 */
export const orcamento: Record<string, string> = {
  // --- Resultado de aplicar os modelos ---
  'Successfully returned funds from $t(budget-template-source, {"count": {{sourceCount}}}) and funded $t(budget-template-sinking-fund, {"count": {{sinkCount}}}).':
    'Os valores voltaram de $t(budget-template-source, {"count": {{sourceCount}}}) e foram aplicados em $t(budget-template-sinking-fund, {"count": {{sinkCount}}}).',
  'Successfully returned funds from $t(budget-template-source, {"count": {{sourceCount}}}) and funded $t(budget-template-sinking-fund, {"count": {{sinkCount}}}). There were errors interpreting some templates:':
    'Os valores voltaram de $t(budget-template-source, {"count": {{sourceCount}}}) e foram aplicados em $t(budget-template-sinking-fund, {"count": {{sinkCount}}}). Alguns modelos deram erro:',
  'Everything is up to date': 'Está tudo em dia',
  'There were errors interpreting some templates:':
    'Alguns modelos deram erro:',
  'All templates passed! 🎉': 'Todos os modelos passaram! 🎉',
  'Global: Funds not available:': 'Geral: saldo insuficiente:',
  'All categories were up to date.': 'Todas as categorias já estavam em dia.',
  'There was an error sorting the categories. Please try again.':
    'Houve um erro ao ordenar as categorias. Tente de novo.',

  // --- Menu da categoria ---
  Goal: 'Meta',
  'Sort A to Z': 'Ordenar de A a Z',
  'Sort Z to A': 'Ordenar de Z a A',
  'active globally': 'ativa em todas as categorias',
  'active in a pool': 'ativa num grupo',

  // --- Erros de modelo ---
  'Unsupported template type: {{ type } satisfies TransObjectLiteral}':
    'Tipo de modelo não suportado: {{ type } satisfies TransObjectLiteral}',
  'Refill needs a balance cap': 'A recarga precisa de um teto de saldo',
  'Percentage out of range': 'Porcentagem fora do intervalo',
  'Source category missing': 'Falta a categoria de origem',
  'Target month missing': 'Falta o mês-alvo',
  'Target is in the past': 'O alvo está no passado',
  'Source category not recognised': 'Categoria de origem não reconhecida',
  'Adjustment out of range': 'Ajuste fora do intervalo',

  // --- Ajuda de preenchimento ---
  'Pick a schedule': 'Escolha um agendamento',
  'Add an automation that contributes funds':
    'Adicione uma automação que aporte valor',
  'Pick a source category': 'Escolha a categoria de origem',
  'Pick a target month': 'Escolha o mês-alvo',
  'Pick an early-spending start month': 'Escolha o mês em que o gasto começa',
  'Pick a valid income category': 'Escolha uma categoria de receita válida',
  'Pick an existing schedule, or create one in Schedules. This automation can&rsquo;t run until it&rsquo;s linked to a schedule.':
    'Escolha um agendamento existente, ou crie um em Agendamentos. Esta automação só roda depois de ligada a um agendamento.',
  'Refill automations must have a &ldquo;Balance cap&rdquo; automation added to use as the target.':
    'A automação de recarga precisa de um &ldquo;teto de saldo&rdquo; pra usar como alvo.',
  'A balance cap on its own does nothing. Add a contributing automation (such as a fixed amount, save by date, or whatever is left) so the cap has something to clamp.':
    'Um teto de saldo sozinho não faz nada. Adicione uma automação que aporte valor (valor fixo, guardar até uma data ou o que sobrar) pra o teto ter o que limitar.',
  'Set a value greater than 0% and at most 100%.':
    'Use um valor maior que 0% e no máximo 100%.',
  'Percentage automations need a source category to calculate against.':
    'A automação por porcentagem precisa de uma categoria de origem pra calcular.',
  'Goals by date need a target month. Pick when you want this fully funded.':
    'Meta por data precisa de um mês-alvo. Escolha quando ela deve estar completa.',
  'Pick a future month, or switch to a recurring annual goal to keep saving.':
    'Escolha um mês no futuro, ou mude pra uma meta anual recorrente pra continuar guardando.',
  'The early-spending month must be the same as or earlier than the target month, since it marks when spending begins.':
    'O mês de início do gasto tem que ser igual ou anterior ao mês-alvo, já que ele marca quando o gasto começa.',
  'Your percent automations add up to more than 100% and will be capped at 100%.':
    'Suas automações por porcentagem somam mais de 100% e serão limitadas a 100%.',

  // --- Tipos de automação ---
  'Fixed amount': 'Valor fixo',
  'Save up for a scheduled transaction.':
    'Guardar para um lançamento agendado.',
  'Save by date': 'Guardar até uma data',
  'Spread a target amount across the months until a deadline.':
    'Distribuir um valor-alvo pelos meses até o prazo.',
  'From history': 'Pelo histórico',
  'Use past months: average, a specific month, or a copy.':
    'Usar meses anteriores: média, um mês específico ou uma cópia.',
  'Stop budgeting to this category once the balance reaches a cap.':
    'Parar de aportar nesta categoria quando o saldo chegar ao teto.',
  'Refill to cap': 'Recarregar até o teto',
  'Top the category back up to the balance cap each month.':
    'Completar a categoria até o teto de saldo todo mês.',
  'Whatever is left': 'O que sobrar',
  'Split any remaining To Budget across these categories.':
    'Dividir o que sobrar de "A orçar" entre estas categorias.',
  'Long-term goal': 'Meta de longo prazo',
  'Set a long-term savings target. This changes the coloring of the balance on the budget page to be based on progress towards the target rather than the current month funding progress.':
    'Define um alvo de longo prazo. A cor do saldo na tela de orçamento passa a mostrar o progresso rumo ao alvo, em vez do quanto foi aportado no mês.',

  // --- Campos ---
  Adjustment: 'Ajuste',
  'No adjustment': 'Sem ajuste',
  Increase: 'Aumentar',
  'Total amount': 'Valor total',
  'Target month': 'Mês-alvo',
  Period: 'Período',
  'Start spending in': 'Começar a gastar em',
  "Without this, spending before the target month leaves a gap that the next month's contribution has to make up. Turn this on to tell Actual spending is expected from a chosen month onwards, so it doesn't budget more to recover the shortfall.":
    'Sem isso, gastar antes do mês-alvo abre um buraco que o aporte do mês seguinte tem que cobrir. Ligue pra avisar que o gasto é esperado a partir do mês escolhido, e o app não aporta a mais pra compensar.',
  'Target amount': 'Valor-alvo',
  'Select a category': 'Escolha uma categoria',
  'Retain existing funds over the cap': 'Manter o que passar do teto',
  'Only enough to cover any overspending':
    'Só o necessário pra cobrir o que passou',
  'No cleanup configured': 'Nenhuma limpeza configurada',
  'Active globally': 'Ativa em todas as categorias',
  'Type to search or create…': 'Digite pra buscar ou criar…',
  Starting: 'A partir de',
  Week: 'Semana',
  weeks: 'semanas',
  months: 'meses',
  years: 'anos',

  // --- Grupos (pools) ---
  'Send leftover': 'Enviar o que sobrar',
  'Receive leftover': 'Receber o que sobrar',
  'Unknown pool': 'Grupo desconhecido',
  'Pool: {{groupName}}': 'Grupo: {{groupName}}',
  'Send leftover to pool': 'Enviar o que sobrar para o grupo',
  'Receive leftover from pool': 'Receber do grupo o que sobrar',
  'Pick or create a pool': 'Escolha ou crie um grupo',
  'Remove pool': 'Remover do grupo',
  Weight: 'Peso',
  'Add to a pool': 'Adicionar a um grupo',
};
